import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Avatar } from '../ui/avatar';
import { toast } from 'sonner';
import { Camera, X } from 'lucide-react';
import { apiConfig } from '../../config/api';

interface CallsignInfo {
  callsign: string;
  nama_lengkap: string;
  foto: string | null;
  jabatan?: string[];
}

export function CallsignLookupPage() {
  const [callsign, setCallsign] = useState('');
  const [result, setResult] = useState<CallsignInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [showQrScanner, setShowQrScanner] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const searchCallsign = useCallback(async (cs: string) => {
    if (!cs.trim()) {
      toast.error('Masukkan callsign');
      return;
    }

    setLoading(true);
    setNotFound(false);
    setResult(null);

    try {
      const url = `${apiConfig.baseURL}/api/public/callsign?callsign=${encodeURIComponent(cs)}`;
      const response = await fetch(url);

      if (!response.ok) {
        if (response.status === 404) {
          setNotFound(true);
          toast.error('Callsign tidak ditemukan');
        } else {
          toast.error('Terjadi kesalahan saat mencari callsign');
        }
        return;
      }

      const data = await response.json();
      setResult(data);
      toast.success('Callsign ditemukan');
    } catch (error) {
      console.error('Error searching callsign:', error);
      toast.error('Gagal mencari callsign');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = useCallback(() => {
    searchCallsign(callsign);
  }, [callsign, searchCallsign]);

  // Load callsign from URL query param on mount and auto-search
  useEffect(() => {
    const url = new URL(window.location.href);
    const callsignParam = url.searchParams.get('callsign');
    if (callsignParam?.trim()) {
      searchCallsign(callsignParam);
    }
  }, [searchCallsign]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch]
  );

  const initializeQrScanner = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setShowQrScanner(true);
        scanQrCode();
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast.error('Tidak dapat mengakses kamera');
    }
  }, []);

  const stopQrScanner = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setShowQrScanner(false);
  }, []);

  const scanQrCode = useCallback(() => {
    if (!showQrScanner) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);

    // Simple QR detection: check for common patterns
    // For production, consider using a proper library like jsQR
    try {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      // This is a simplified approach - you might want to use jsQR library
      // For now, we'll just continue scanning
      requestAnimationFrame(scanQrCode);
    } catch (error) {
      requestAnimationFrame(scanQrCode);
    }
  }, [showQrScanner]);

  // Parse QR code data format: "CALLSIGN | NAMA LENGKAP |"
  const parseQrData = useCallback(
    (qrData: string) => {
      try {
        const parts = qrData.split('|').map((p) => p.trim());
        if (parts.length >= 1 && parts[0]) {
          setCallsign(parts[0]);
          // Trigger search with extracted callsign
          setTimeout(() => {
            setCallsign(parts[0]);
          }, 0);
          return true;
        }
      } catch {
        return false;
      }
      return false;
    },
    []
  );

  // Handle paste event for QR code scanning (e.g., from barcode scanner apps)
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const pastedText = e.clipboardData?.getData('text') || '';
      if (pastedText && pastedText.includes('|')) {
        e.preventDefault();
        if (parseQrData(pastedText)) {
          toast.success('QR code terdeteksi');
        }
      }
    };

    if (showQrScanner) {
      document.addEventListener('paste', handlePaste);
      return () => {
        document.removeEventListener('paste', handlePaste);
      };
    }
  }, [showQrScanner, parseQrData]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 p-4">
      <div className="max-w-md mx-auto pt-20">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            Cari Callsign
          </h1>
          <p className="text-slate-600">
            Masukkan callsign untuk melihat informasi anggota
          </p>
        </div>

        {/* Search Card */}
        <Card className="p-6 shadow-lg">
          <div className="space-y-4">
            {/* Input */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Callsign
              </label>
              <Input
                placeholder="e.g., YC0RXI"
                value={callsign}
                onChange={(e) => setCallsign(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
                className="uppercase"
              />
            </div>

            {/* Search Button */}
            <div className="flex gap-2">
              <Button
                onClick={() => handleSearch()}
                disabled={loading}
                className="flex-1"
              >
                {loading ? 'Mencari...' : 'Cari'}
              </Button>
              <Button
                onClick={initializeQrScanner}
                disabled={loading || showQrScanner}
                variant="outline"
                className="px-3"
                title="Scan QR Code"
              >
                <Camera className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Card>

        {/* QR Scanner */}
        {showQrScanner && (
          <Card className="mt-6 p-6 shadow-lg">
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-slate-900">Scan KTA QR Code</h3>
                <button
                  onClick={stopQrScanner}
                  className="text-slate-500 hover:text-slate-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="w-full rounded-lg border border-slate-300 bg-slate-900"
              />
              <canvas ref={canvasRef} className="hidden" />

              <p className="text-sm text-slate-600 text-center">
                Arahkan kamera ke QR code pada KTA
              </p>
            </div>
          </Card>
        )}

        {/* Not Found Message */}
        {notFound && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">
              Callsign "{callsign.toUpperCase()}" tidak ditemukan dalam sistem.
            </p>
          </div>
        )}

        {/* Result Card */}
        {result && (
          <Card className="mt-6 p-6 shadow-lg animate-in fade-in slide-in-from-bottom-4">
            <div className="space-y-4">
              {/* Avatar */}
              <div className="flex justify-center">
                <Avatar
                  className="h-24 w-24"
                  src={result.foto || undefined}
                  alt={result.nama_lengkap}
                  fallback={result.callsign.substring(0, 2)}
                />
              </div>

              {/* Info */}
              <div className="space-y-3 text-center">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">
                    Callsign
                  </p>
                  <p className="text-2xl font-bold text-slate-900 font-mono">
                    {result.callsign}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">
                    Nama Lengkap
                  </p>
                  <p className="text-lg font-semibold text-slate-800">
                    {result.nama_lengkap}
                  </p>
                </div>

                {result.jabatan && result.jabatan.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">
                      Jabatan
                    </p>
                    <p className="text-sm font-semibold text-red-600">
                      {result.jabatan.join(', ')}
                    </p>
                  </div>
                )}
              </div>

              {/* New Search Button */}
              <Button
                variant="outline"
                onClick={() => {
                  setResult(null);
                  setCallsign('');
                  setNotFound(false);
                }}
                className="w-full mt-6"
              >
                Cari Callsign Lain
              </Button>
            </div>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-sm text-slate-500 mt-8">
          Halaman pencarian publik - tidak memerlukan login
        </p>
      </div>
    </div>
  );
}
